import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';
import { supabase } from '@/integrations/supabase/client';

/** Version dieser konkreten Umfrage. Pro Nutzer und Version genau ein Zustand. */
export const USAGE_SURVEY_VERSION = 'usage_barriers_2026_09_v1';

/** Nur Bestandsnutzer: Account vor dem 07.09.2026 erstellt. */
const CUTOFF_ISO = '2026-09-07T00:00:00.000Z';

const Q1_OPTIONS = [
  { value: 'manual', label: 'Manuell' },
  { value: 'scolia', label: 'Scolia' },
  { value: 'autodarts', label: 'Autodarts' },
  { value: 'other_auto', label: 'Anderes automatisches System' },
  { value: 'none', label: 'Gar nicht' },
] as const;

const Q2_OPTIONS = [
  { value: 'manual_entry', label: 'Ich müsste meine Würfe zusätzlich manuell eingeben' },
  { value: 'value', label: 'Ich sehe für mich noch nicht genug Nutzen' },
  { value: 'usability', label: 'Die Bedienung ist mir zu aufwendig' },
  { value: 'training_fit', label: 'Das Training passt nicht zu mir' },
  { value: 'price', label: 'Der Preis' },
  { value: 'time', label: 'Ich hatte bisher einfach keine Zeit' },
  { value: 'technical', label: 'Ein technisches Problem' },
  { value: 'other', label: 'Anderer Grund' },
] as const;

const Q3_OPTIONS = [
  { value: 'yes', label: 'Ja' },
  { value: 'maybe', label: 'Vielleicht' },
  { value: 'no', label: 'Nein' },
] as const;



const OptionButton: React.FC<{ label: string; selected: boolean; onClick: () => void }> = ({
  label,
  selected,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full rounded-xl border px-4 py-3 text-left text-sm transition-colors"
    style={{
      background: selected ? 'rgba(52,210,122,0.12)' : 'rgba(255,255,255,0.03)',
      borderColor: selected ? '#34d27a' : '#243140',
      color: selected ? '#e9eff6' : '#c3ccd6',
      minHeight: 0,
    }}
  >
    {label}
  </button>
);

export const UsageSurveyDialog: React.FC = () => {
  const { user } = useAuth();
  const { isAdmin, isLoading: roleLoading } = useUserRole();

  const [eligible, setEligible] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const savedRef = useRef(false);

  const [q1, setQ1] = useState<string | null>(null);
  const [q2, setQ2] = useState<string | null>(null);
  const [q3, setQ3] = useState<string | null>(null);
  const [freeText, setFreeText] = useState('');

  const showFreeText = q2 === 'other';
  const canSubmit = useMemo(() => !!q1 && !!q2 && !!q3, [q1, q2, q3]);

  // Berechtigung prüfen (Bestandsnutzer, kein Admin, noch kein Zustand vorhanden).
  useEffect(() => {
    let cancelled = false;
    if (!user?.id || roleLoading || isAdmin) return;

    (async () => {
      const [{ data: profile, error: profileError }, { data: existing, error: existingError }] = await Promise.all([
        supabase.from('profiles').select('created_at').eq('user_id', user.id).maybeSingle(),
        supabase
          .from('survey_responses')
          .select('id')
          .eq('user_id', user.id)
          .eq('survey_version', USAGE_SURVEY_VERSION)
          .maybeSingle(),
      ]);

      if (cancelled) return;
      if (profileError || existingError) return; // fail closed
      if (existing) return;
      if (!profile?.created_at) return;
      if (new Date(profile.created_at).getTime() >= new Date(CUTOFF_ISO).getTime()) return;

      setEligible(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, roleLoading, isAdmin]);

  // Nicht sofort beim Login: kleine Verzögerung nach vollständigem Laden.
  useEffect(() => {
    if (!eligible) return;
    const timer = window.setTimeout(() => setOpen(true), 2500);
    return () => window.clearTimeout(timer);
  }, [eligible]);

  const persist = async (status: 'submitted' | 'dismissed') => {
    if (savedRef.current || !user?.id) return;
    savedRef.current = true;
    setSaving(true);
    const now = new Date().toISOString();
    const { error } = await supabase.from('survey_responses').upsert(
      {
        user_id: user.id,
        survey_version: USAGE_SURVEY_VERSION,
        status,
        submitted_at: status === 'submitted' ? now : null,
        dismissed_at: status === 'dismissed' ? now : null,
        q1_capture: status === 'submitted' ? q1 : null,
        q2_barrier: status === 'submitted' ? q2 : null,
        q3_auto_more: status === 'submitted' ? q3 : null,
        other_reason_text:
          status === 'submitted' && showFreeText && freeText.trim() ? freeText.trim().slice(0, 500) : null,
      },
      { onConflict: 'user_id,survey_version' },
    );
    if (error) {
      console.error('UsageSurveyDialog: failed to save response', error);
      savedRef.current = false;
    }
    setSaving(false);
    setEligible(false);
    setOpen(false);
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) void persist('dismissed'); }}>
      <DialogContent
        className="max-w-[520px] max-h-[85vh] overflow-y-auto rounded-2xl border p-5 sm:p-7"
        style={{ background: '#121a25', borderColor: '#243140' }}
      >
        <DialogHeader>
          <DialogTitle
            className="text-left text-lg sm:text-xl"
            style={{ fontFamily: 'Archivo, sans-serif', color: '#e9eff6' }}
          >
            Kurze Frage an dich
          </DialogTitle>
          <DialogDescription className="text-left text-sm leading-relaxed" style={{ color: '#93a1b1' }}>
            Zwei kurze Fragen helfen uns, AimUp besser zu machen. Dauert unter einer Minute.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-5">
          <div className="space-y-2">
            <p className="text-sm font-semibold" style={{ color: '#e9eff6' }}>
              Wie erfasst du deine Würfe normalerweise?
            </p>
            <div className="space-y-2">
              {Q1_OPTIONS.map((o) => (
                <OptionButton key={o.value} label={o.label} selected={q1 === o.value} onClick={() => setQ1(o.value)} />
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-semibold" style={{ color: '#e9eff6' }}>
              Was ist aktuell der wichtigste Grund, warum du AimUp nicht häufiger nutzt?
            </p>
            <div className="space-y-2">
              {Q2_OPTIONS.map((o) => (
                <OptionButton key={o.value} label={o.label} selected={q2 === o.value} onClick={() => setQ2(o.value)} />
              ))}
            </div>
          </div>

          {showFreeText && (
            <div className="space-y-2">
              <label className="text-sm font-semibold" style={{ color: '#e9eff6' }} htmlFor="survey-free-text">
                Wenn du möchtest, sag uns kurz warum.
              </label>
              <textarea
                id="survey-free-text"
                value={freeText}
                maxLength={500}
                rows={3}
                onChange={(e) => setFreeText(e.target.value)}
                className="w-full rounded-xl border px-4 py-3 text-sm outline-none"
                style={{ background: 'rgba(255,255,255,0.03)', borderColor: '#243140', color: '#e9eff6' }}
              />
              <p className="text-right text-xs" style={{ color: '#6f7d8c' }}>{freeText.length}/500</p>
            </div>
          )}

          <div className="space-y-2">
            <p className="text-sm font-semibold" style={{ color: '#e9eff6' }}>
              Wenn AimUp deine Würfe automatisch übernehmen könnte, würdest du AimUp voraussichtlich häufiger nutzen?
            </p>
            <div className="space-y-2">
              {Q3_OPTIONS.map((o) => (
                <OptionButton key={o.value} label={o.label} selected={q3 === o.value} onClick={() => setQ3(o.value)} />
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-col items-center gap-3">
          <button
            type="button"
            disabled={!canSubmit || saving}
            onClick={() => void persist('submitted')}
            className="w-full rounded-xl px-4 py-3 text-sm font-semibold transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            style={{ background: '#34d27a', color: '#0b1119' }}
          >
            Antwort senden
          </button>
          <button
            type="button"
            onClick={() => void persist('dismissed')}
            className="text-xs underline underline-offset-4"
            style={{ minHeight: 0, padding: 0, background: 'transparent', color: '#93a1b1' }}
          >
            Später nicht mehr fragen
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
